import React, { useState, useCallback, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import useProductSearch from './hooks/useProductSearch';
import EmptyState from './EmptyState';
import { FS, AUTOCOMPLETE_POPPER_MIN_WIDTH_SX } from '../../theme/tokens';
import { v2Tone } from '../../theme/theme';

/**
 * ProductSearchSelect — debounced product autocomplete (name / variant / vendor / SKU).
 *
 * @description
 *   Searches /api/search/products. Returns a normalized product object
 *   {sku, product_name, variant_name, product_handle, vendor_name, vendor_id,
 *   cost_per_item, currency, product_status, current_inventory, product_id,
 *   product_row_id} — `product_id` ist die SHOPIFY-Id, `product_row_id` die
 *   Zeilen-Id `shp_products.id` (seit EK-C3; wer auf eine Produktzeile
 *   verweist, braucht diese). Einträge aus „zuletzt gewählt" von vor EK-C3
 *   tragen keine `product_row_id`.
 *   via onChange.
 *   Shows recent selections when the input is empty, and an optional "Add new
 *   product" footer that lets the parent open a create-product flow. SKU is
 *   the canonical dedup key. Draft-status products are flagged with a "Draft"
 *   chip so operators can tell an unlisted product apart from a live one.
 *
 * @useWhen
 *   - Operator needs to find a product by name / variant / vendor / SKU
 *   - Flow should optionally fall through to a create-product step (onAddNew)
 *   - Server-side debounce + abort-on-restart + recent picks are desired
 *
 * @dontUseFor
 *   - Picking a vendor or PO (this is product-scoped)
 *   - Bulk multi-select (this is single-value)
 *   - Compatibility-list-only flows (use a static list, not search)
 *
 * @related
 *   - MUI Autocomplete — the underlying primitive
 *   - useProductSearch — the hook this component composes around
 *
 * @reuseHints
 *   - PO line items: pass `vendorFilter=poVendorName`
 *   - B2B order form: hide `onAddNew` (no in-flow product creation)
 *   - RMA picker: pass `excludeSkus=[currentRowsSkus]` to grey duplicates
 *
 * @props
 *   - value: selected product object | null — its label is written into the
 *     input field, so a pre-filled row shows the product it carries. Text the
 *     operator has typed but not confirmed is never overwritten by it, and is
 *     cleared when the field loses focus (MUI clearOnBlur).
 *   - onChange(product|null): selection callback
 *   - onAddNew?: () => void — when provided, dropdown shows "Add new product" footer
 *   - excludeSkus?: string[] — greys out matching options (still rendered, tooltip)
 *   - vendorFilter?: string — server-side vendor_name ILIKE filter
 *   - placeholder, disabled, size, sx: standard; ariaLabel?: string — the combobox's accessible name, per MEANING not per object (set by all seven call sites since G15e, required from G16)
 *
 *   🔴 Das Löschkreuz im Feld ist ebenfalls ein VERTRAG dieses Bausteins
 *   (UI2 Teil 10, G15e NB3 / R16-B1). Solange Text im Feld steht und **kein**
 *   `value` gewählt ist, trägt das Feld einen echten `IconButton` ✕ mit
 *   `aria-label="Clear search"` — derselbe Name wie im Leerzustand und in
 *   `SearchTrigger.jsx:53-59`, weil mehrere Namen für eine Handlung die
 *   schlechtere Auskunft wären. Er leert Feld **und** Abfrage (`clearQueryAndFocus`)
 *   und gibt den Fokus ins Feld zurück, damit ein Tastaturnutzer nach dem
 *   Leeren nicht auf `document.body` fällt. 🔴 Bei gesetztem `value` bleibt er
 *   WEG: dann zeigt MUI sein eigenes `clear`, und zwei ✕ dürfen nie
 *   nebeneinander stehen.
 *
 *   🔴 Er ist per **Tab** erreichbar (G15e NB5). Der Fokuswechsel vom Feld auf
 *   dieses ✕ ist **kein** Verlassen des Bausteins: `handleInputChange` erkennt
 *   ihn am `relatedTarget` des Blur-Events und unterlässt das Leeren, sonst
 *   hinge MUI das ✕ im selben Zug ab, bevor es den Fokus bekommt. Erst wer den
 *   Baustein wirklich verlässt (Tab hinter dem ✕, Shift+Tab davor, Klick nach
 *   draußen), leert wie bisher — das Verhalten der Aufrufer beim Verlassen
 *   bleibt unverändert. Enter/Leertaste auf dem ✕ leeren — getragen vom nativen
 *   Klick des `<button>`; der `onKeyDown` unten ist Vorsorge, und dass er den
 *   Tastendruck vom `Autocomplete` fernhält, ist NICHT gemessen (R15-W1).
 *   MUI's eigenes `clearOnEscape` ist NICHT gesetzt.
 *
 *   🔴 Der Leerzustand der Trefferliste ist ein VERTRAG dieses Bausteins, nicht
 *   des Aufrufers (UI2 Teil 10, G15e / §2.6 Bauentscheid 3). Vier Lagen, und
 *   nur eine davon ist gar kein Zustand:
 *   - weniger als 2 Zeichen → eine ANLEITUNG („Type at least 2 characters…"),
 *     kein Zustand: P9 kennt dafür keinen Grund, und `EmptyState` verlangt
 *     einen. Bleibt deshalb reiner Text.
 *   - Suche gescheitert (`error` aus dem Hook) → `EmptyState reason="error"`
 *     mit `message="Search is unavailable right now. Type again to try."` und `onRetry`
 *     (`search(query)` — derselbe Begriff, sonst liefe der debouncte Effekt
 *     nicht). 🔴 NIE „No product matches": leer und Fehler sind verschiedene
 *     Objekte (Regel 22 §4/§5).
 *   - Suche läuft → `EmptyState reason="unknown"` mit ausdrücklichem
 *     `message="Searching…"`. 🔴 `query` wird hier NICHT übergeben —
 *     `EmptyState` warnt bei `query` unter einem Grund ≠ `no-match`.
 *     🔴 Und `loading` steht aus demselben Grund NICHT am `Autocomplete`:
 *     gemessen rendert MUI sonst seine eigene Zeile ANSTELLE von
 *     `noOptionsText`, und dieser Zustand käme nie ins DOM (Jest-Probe
 *     `ProductSearchSelect.leerzustand.test.jsx`, Fall „Suche laeuft").
 *     Der Fortschritt bleibt im Feld sichtbar (`endAdornment`).
 *   - sonst → `EmptyState reason="no-match"` mit `query` und `onClearSearch`
 *     (`clearQueryAndFocus`: leert Feld UND Abfrage, gibt den Fokus ins Feld).
 *     Beides ist Pflicht: ohne den Ausweg wäre das Echo eine Sackgasse
 *     (R16-B1).
 *   Die Trefferlage trägt genau ein `[data-reason="no-match"]` im Dropdown —
 *   der Anker, an dem P9 eine Dialog-Suchliste prüft.
 */
/**
 * Shopify names the single variant of EVERY one-variant product "Default
 * Title" — a platform placeholder, not a variant anybody chose. It is hidden
 * from labels here so it can never be read as a product name.
 *
 * 🔴 DISPLAY LAYER ONLY. This test must never travel into a path that writes,
 * deletes or classifies. Doing exactly that cost real variants in production on
 * 2026-07-15 — see docs/knowledge/operations/data-quirks.md,
 * §"variants/bulk deleted the REAL lone variant when adding a colour".
 * Deliberately module-local and NOT exported (grill 2026-08-20, W3).
 */
const isPlaceholderVariantName = (name) =>
  typeof name === 'string' && name.trim().toLowerCase() === 'default title';

export default function ProductSearchSelect({
  value = null,
  onChange,
  onAddNew = null,
  excludeSkus = [],
  vendorFilter = null,
  placeholder = 'Search by name, variant, vendor, or SKU…',
  disabled = false,
  size = 'small',
  sx = {},
  ariaLabel = null,
}) {
  const [inputValue, setInputValue] = useState('');
  const {
    query,
    setQuery,
    results,
    loading,
    error,
    recentProducts,
    addToRecent,
    clearSearch,
    search,
  } = useProductSearch({ vendorFilter });

  // Excluded-SKU set for O(1) lookup in renderOption + handleChange
  const excludeSet = React.useMemo(() => new Set(excludeSkus || []), [excludeSkus]);

  const options = React.useMemo(() => {
    if (query.trim().length >= 2) return results;
    return recentProducts;
  }, [query, results, recentProducts]);

  // `inputValue` is controlled here, which overrules MUI's own derivation from
  // `value` — every character in the field has to come through this handler.
  // Before 2026-08-21 it only knew 'input' and 'clear', so a `value` handed in
  // from the parent never reached the field: the RMA replacement row showed an
  // empty search box next to a product it already carried.
  //
  // 🔴 Provenance guard (grill 2026-08-20, BLOCKER 1): MUI fires
  // `reason: 'reset'` whenever the *identity* of `value` changes — focused
  // field included (useAutocomplete.js :198-210 skips the effect only when the
  // value did NOT change). A parent that rebuilds its value object on each
  // render would therefore overwrite what the operator is typing. So a 'reset'
  // is applied only while the field holds text that this component itself
  // wrote from `value`; unconfirmed operator input wins over it.
  const inputValueRef = useRef('');
  const valueLabelRef = useRef('');
  // Das Eingabeelement selbst — Ziel des Fokus-Rückwegs am ✕ (`clearQueryAndFocus`).
  const inputRef = useRef(null);
  // Das eigene ✕ — der Unterscheider im Blur-Grund: geht der Fokus DORTHIN,
  // ist das kein Verlassen des Feldes (siehe `handleInputChange`).
  const clearButtonRef = useRef(null);

  const writeInput = useCallback((next, fromValue) => {
    inputValueRef.current = next;
    if (fromValue) valueLabelRef.current = next;
    setInputValue(next);
  }, []);

  const handleInputChange = useCallback((event, newValue, reason) => {
    if (reason === 'input') {
      writeInput(newValue, false);
      setQuery(newValue);
      return;
    }
    if (reason === 'clear') {
      writeInput('', true);
      clearSearch();
      return;
    }
    // 'selectOption' — the label of the option just picked. The field keeps the
    // focus during a pick, so the provenance guard below would refuse it.
    // 'blur' — MUI's clearOnBlur: unconfirmed text goes, the committed value's
    // label stays. clearSearch() so reopening the list shows recent picks
    // instead of the stale hit list of the abandoned query (grill W1).
    //
    // 🔴 AUSNAHME, und sie ist der ganze Punkt von G15e NB5: MUI ruft diesen
    // Grund mit dem NATIVEN blur-Event auf, und `relatedTarget` nennt das
    // Element, das den Fokus bekommt (gemessen an MUI 7, Jest-Probe
    // `ProductSearchSelect.loeschkreuz.test.jsx` „Tab vom Feld ...": `type` =
    // `blur`, `relatedTarget` = unser eigener Knopf). Wandert der Fokus auf das
    // EIGENE ✕, ist das kein Verlassen des Feldes — ohne diese Zeile setzt MUI
    // `inputValue` auf '' und haengt den Knopf im selben Zug ab, BEVOR er den
    // Fokus bekommt: der Tab landet auf `document.body`, und der Tastaturweg
    // zum Leeren ist tot. Alle anderen Blur-Ziele (auch ein Fokuswechsel nach
    // draussen) leeren weiterhin wie bisher.
    if (
      reason === 'blur'
      && event
      && event.relatedTarget
      && event.relatedTarget === clearButtonRef.current
    ) {
      return;
    }
    if (reason === 'selectOption' || reason === 'blur') {
      writeInput(newValue, true);
      if (!newValue) clearSearch();
      return;
    }
    if (reason === 'reset') {
      // 🔴 Liegt der Fokus auf dem EIGENEN ✕, darf ein Reset den Text nicht
      // unter dem Knopf wegziehen (MUI feuert 'reset' unmittelbar nach dem
      // Blur, den die Ausnahme oben stehen lässt). Sonst fiele der Fokus auf
      // `document.body`, genau wie vor NB5.
      if (clearButtonRef.current && document.activeElement === clearButtonRef.current) return;
      const current = inputValueRef.current;
      if (current !== '' && current !== valueLabelRef.current) return;
      writeInput(newValue, true);
      if (!newValue) clearSearch();
    }
  }, [setQuery, clearSearch, writeInput]);

  const handleChange = useCallback((_, newValue) => {
    if (newValue && excludeSet.has(newValue.sku)) {
      // Defense in depth — MenuItem is also disabled, but never let an
      // excluded SKU through the callback.
      return;
    }
    if (newValue) addToRecent(newValue);
    // 🔴 Seit NB5 kann das ✕ den Fokus halten. Wird von dort eine Option
    // gewaehlt (Pfeiltaste + Enter), haengt der Knopf mit `value` ab — ohne
    // diesen Rueckweg faellt der Fokus auf `document.body`. Synchron im
    // Handler, also bevor React neu rendert (Muster `clearQueryAndFocus`).
    if (
      newValue
      && clearButtonRef.current
      && document.activeElement === clearButtonRef.current
      && inputRef.current
    ) {
      inputRef.current.focus();
    }
    if (onChange) onChange(newValue);
  }, [onChange, addToRecent, excludeSet]);

  // Der Ausweg aus dem Leerzustand („Clear search" im Dropdown). Derselbe
  // Griff wie MUI's eigenes `clear`: Feld UND Abfrage. Nur `clearSearch()`
  // liesse den Begriff im Feld stehen, zu dem die Liste gerade „nichts
  // gefunden" gesagt hat — das Feld behauptete dann etwas anderes als die
  // Liste darunter.
  // 🔴 Seit NB7 läuft dieser Weg über `clearQueryAndFocus`: BEIDE Leerwege
  // geben den Fokus ins Feld zurück (R16-Runde 2 W2). Der Popper-Knopf ist kein
  // Tab-Ziel; der Fokus liegt dort schon im Feld (MUI verhindert den Wechsel).
  // Jest misst die REIHENFOLGE (`focus()` vor dem Leeren) — das macht ihn unabhängig.
  const clearQuery = useCallback(() => {
    writeInput('', true);
    clearSearch();
  }, [writeInput, clearSearch]);

  // Der Tastaturweg zum ✕ (R16-B1, UI2 Teil 10 G15e NB3). Der Knopf hängt an
  // `{inputValue && !value}`: mit dem Leeren wird er ABGEHÄNGT. Liegt der Fokus
  // auf ihm (Tastatur), setzt der Browser ihn danach auf `document.body` und
  // der nächste `Tab` begänne am Dokumentanfang. `focus()` läuft SYNCHRON im
  // Handler, also bevor React neu rendert — dasselbe Muster wie
  // `SearchTrigger.jsx:582-599`. Auf dem ZEIGERWEG liegt der Fokus ohnehin
  // schon im Feld (`onMouseDown`+`preventDefault` nimmt dem Knopf den
  // Mausfokus), dort ist der Aufruf wirkungslos.
  const clearQueryAndFocus = useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
    clearQuery();
  }, [clearQuery]);

  const getOptionLabel = useCallback((option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    const parts = [option.product_name];
    // Placeholder variant suppressed — otherwise the fix above writes
    // "… (Default Title) - SKU" straight into the field it just made visible
    // (grill 2026-08-20, BLOCKER 3).
    if (option.variant_name && !isPlaceholderVariantName(option.variant_name)) {
      parts.push(`(${option.variant_name})`);
    }
    if (option.sku) parts.push(`- ${option.sku}`);
    return parts.join(' ');
  }, []);

  const isOptionEqualToValue = useCallback((option, val) => {
    if (!option || !val) return false;
    return option.sku === val.sku;
  }, []);

  const renderOption = useCallback((props, option) => {
    const isRecent = !query.trim() && recentProducts.some(p => p.sku === option.sku);
    const isExcluded = excludeSet.has(option.sku);
    const tooltip = isExcluded ? 'Already added in this picker' : '';

    const inner = (
      <ListItem
        {...props}
        key={option.sku}
        dense
        disabled={isExcluded}
        aria-disabled={isExcluded || undefined}
        sx={isExcluded ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
          {isRecent && <HistoryIcon sx={{ color: 'text.secondary', fontSize: 18 }} />}
          <ListItemText
            primary={
              <Typography variant="body2" noWrap>
                {option.product_name}
                {option.variant_name && !isPlaceholderVariantName(option.variant_name) && (
                  <Typography component="span" color="text.secondary">
                    {' '}({option.variant_name})
                  </Typography>
                )}
              </Typography>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.sku && `SKU: ${option.sku}`}
                {option.vendor_name && ` · ${option.vendor_name}`}
              </Typography>
            }
          />
          {String(option.product_status).toLowerCase() === 'draft' && (
            <Chip
              label="Draft"
              size="small"
              variant="outlined"
              // height 18 → 20 with the PR-B scale raise: FS.sm went 11 → 12.5, so
              // the box grows by the same 1.5 px (rounded to 2) and the chip keeps
              // the 7 px of air it was designed with. Fixed here, not in a theme
              // MuiChip override — `styleFunctionSx` is appended last in
              // createStyled, so an sx `height` always beats the theme.
              // K-3: umrandet = Schrift `palette.warning.main` auf Papier (3,113:1
              // hell); der v2-Ton traegt in beiden Modi.
              sx={(t) => ({ color: v2Tone(t, 'warn').main, borderColor: v2Tone(t, 'warn').main, height: 20, flexShrink: 0, '& .MuiChip-label': { px: 0.75, fontSize: FS.sm } })}
            />
          )}
        </Box>
      </ListItem>
    );
    return isExcluded ? <Tooltip title={tooltip} placement="left">{inner}</Tooltip> : inner;
  }, [query, recentProducts, excludeSet]);

  const renderPaper = useCallback((paperProps) => {
    const showRecentHeader = !query.trim() && recentProducts.length > 0;
    // Unified microcopy: footer is always "+ Add new product". The
    // zero-results case still routes here — operator gets the same CTA.
    const footerLabel = '+ Add new product';

    // Activation handler — fires on click (mouse) AND Enter/Space (keyboard).
    // onMouseDown + preventDefault: stops MUI Autocomplete from closing the
    // dropdown before onClick lands; the actual activation runs in onClick.
    const fireAddNew = () => { if (onAddNew) onAddNew(); };

    return (
      <Paper {...paperProps} elevation={8}>
        {showRecentHeader && (
          <>
            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Recent Selections
              </Typography>
            </Box>
            <Divider />
          </>
        )}
        {paperProps.children}
        {onAddNew && (
          <>
            <Divider />
            <MenuItem
              onMouseDown={(e) => e.preventDefault()}
              onClick={fireAddNew}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fireAddNew();
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Add a new product not in the catalog"
              sx={{ color: 'primary.main', fontSize: FS.md }}
            >
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              {footerLabel}
            </MenuItem>
          </>
        )}
      </Paper>
    );
  }, [query, recentProducts.length, onAddNew]);

  // 🔴 Die Popper-Mindestbreite steht als Token in `theme/tokens.js` (Regel 22):
  // MUI nimmt sonst die Breite des Ankers, und in einer engen Zeile brach der
  // Leerzustand in vier Zeilen (R16-W3). Ab `sm` aendert der Token nichts, weil
  // das Feld dort ≥ 300 px ist.
  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      PaperComponent={renderPaper}
      // 🔴 `loading` steht bewusst NICHT am Autocomplete: MUI rendert dann
      // seine eigene Ladezeile ANSTELLE von `noOptionsText` (gemessen), und
      // der „Searching…"-Zustand kaeme nie ins DOM. Der Fortschritt steht
      // sichtbar im Feld (`endAdornment`, unten); die Liste sagt ihn mit
      // Grund (`unknown`) statt mit einem nackten „Loading…".
      disabled={disabled}
      filterOptions={(x) => x} // Server-side filtered; disable client-side filter
      noOptionsText={
        query.trim().length < 2 ? (
          // Anleitung, kein Zustand: P9 kennt für „noch nichts getippt" keinen
          // Grund, und `EmptyState` verlangt einen. Siehe @props.
          'Type at least 2 characters to search…'
        ) : loading ? (
          // 🔴 `query` wird hier NICHT übergeben: `EmptyState` warnt bei
          // `query` unter einem Grund != `no-match`, weil das Echo „nichts
          // gefunden" behauptet, während die Suche noch läuft.
          <EmptyState reason="unknown" message="Searching…" />
        ) : error ? (
          // 🔴 Regel 22 §Component rules 4/5: leer und Fehler sind verschiedene
          // Objekte. Ein 500er oder ein Netzfehler behauptete bis G15e NB3
          // „No product matches “…”." — also, die Suche habe geantwortet.
          // `showDuringOutage`: der globale Ausfall-Banner liegt unter jedem
          // Modal (`bannerEbene` = `zIndex.modal - 1`), dieser Popper aber
          // darueber; ohne die Prop rendert `EmptyState` bei Ausfall `null`
          // und das Dropdown bliebe leer. Die globale Suche
          // (`SidebarAndHeaderBar.jsx`) traegt denselben Grundsatz; hier kommt
          // der TASTATURWEG hinzu (R16-W1, Regel-15-Runde 4 W2): der Knopf
          // steht im Popper, ist also per Tab nicht erreichbar; „Type again to
          // try." nennt die Handlung, die ohne Maus funktioniert. Der Knopf
          // bleibt fuer den Zeigerweg.
          <EmptyState
            reason="error"
            message="Search is unavailable right now. Type again to try."
            showDuringOutage
            onRetry={() => search(query)}
          />
        ) : (
          <EmptyState
            reason="no-match"
            query={query}
            noun="product"
            onClearSearch={clearQueryAndFocus}
          />
        )
      }
      slotProps={{ popper: { sx: AUTOCOMPLETE_POPPER_MIN_WIDTH_SX } }}
      sx={{ minWidth: { xs: 0, sm: 300 }, ...sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
          // 🔴 Der Platzhalter ist KEIN zugaenglicher Name (er verschwindet beim
          // Tippen und wird von Screenreadern uneinheitlich gelesen). Ohne
          // `ariaLabel` hat dieses Feld gar keinen — gemessen 2026-08-22 vom
          // Regel-16-Gate: `aria-label` null, `aria-labelledby` null,
          // `input.labels.length` 0, in drei Engines. Sobald zwei dieser
          // Waehler auf einer Seite stehen und ENTGEGENGESETZTE Kanten
          // schreiben, ist das WCAG 4.1.2/3.3.2 und praktisch gefaehrlich.
          inputProps={{
            ...params.inputProps,
            ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} sx={{ mr: 1 }} />}
                {/* 🔴 Das eigene ✕ steht VOR `params.InputProps.endAdornment`,
                    also vor MUIs eigenem `clear`. Es erscheint nur, solange
                    Text im Feld steht UND kein `value` gewählt ist — bei
                    gesetztem `value` zeigt MUI sein `clear`, und zwei ✕
                    stünden nie nebeneinander. */}
                {inputValue && !value && (
                  <IconButton
                    ref={clearButtonRef}
                    size="small"
                    aria-label="Clear search"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={clearQueryAndFocus}
                    // 🔴 MUI leert beim Blur nur, wenn der BLUR VOM EINGABEFELD
                    // kommt (gemessen: ein Blur vom ✕ nach draussen ruft
                    // `onInputChange` NICHT). Der Knopf ist seit NB5 ein
                    // Tab-Ziel; wer hinter ihn tabbt oder daneben klickt,
                    // verlaesst den Baustein und muss denselben Rueckweg
                    // bekommen wie vorher — deshalb hier ausdruecklich. Zurueck
                    // ins Feld (Shift+Tab) ist KEIN Verlassen und bleibt stehen.
                    onBlur={(e) => {
                      if (e.relatedTarget === inputRef.current) return;
                      clearQuery();
                    }}
                    // 🔴 Defensiv, NICHT lasttragend (R15-W1): der native Klick
                    // eines `<button>` traegt Enter und Leertaste allein —
                    // Mutation C aus NB5 (Zweig auf `false && …`) blieb gruen.
                    // Die Zweige bleiben als Vorsorge: ein Tastendruck soll
                    // nicht zum `Autocomplete` blubbern, dessen `handleKeyDown`
                    // bei offener Liste mit markiertem Eintrag die Option
                    // waehlt. 🔴 Diese zweite Haelfte ist NICHT gemessen.
                    // Dieselbe Form wie der „+ Add new product"-Eintrag weiter
                    // unten in `renderPaper`.
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        clearQueryAndFocus();
                      }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
